import { HttpService } from 'app/services';

export const readDrugIncludeInScriptRequestFromDrug = (drug) => {
  if (!drug) {
    return undefined;
  }

  const raw =
    drug.includeInScriptRequest ??
    drug.IncludeInScriptRequest ??
    drug.drugIncludeInScriptRequest ??
    drug.DrugIncludeInScriptRequest;

  if (raw === true || raw === false) {
    return raw;
  }
  if (raw === 'true' || raw === 'false') {
    return raw === 'true';
  }
  if (raw === 1 || raw === 0) {
    return raw === 1;
  }

  return undefined;
};

export const resolveDrugIncludeInScriptRequestForForm = (drug, fallback = true) => {
  const value = readDrugIncludeInScriptRequestFromDrug(drug);
  return value === undefined ? fallback : value;
};

export const resolveDrugIncludeInScriptRequestWithFetch = async (drug) => {
  let drugInclude = readDrugIncludeInScriptRequestFromDrug(drug);
  const drugId = drug?.drugId || drug?.id;

  if (drugInclude === undefined && drugId) {
    try {
      const resp = await HttpService.get(`api/drugs/${drugId}`);
      drugInclude = readDrugIncludeInScriptRequestFromDrug(resp.data);
    } catch (err) {
      // ignore fetch failure; fallback below
    }
  }

  return drugInclude ?? true;
};

export const hasExplicitIncludeInScriptRequest = (value) =>
  value === true || value === false || value === 'true' || value === 'false' || value === 1 || value === 0;

export const toIncludeInScriptRequestBoolean = (value, defaultValue = true) => {
  if (value === true || value === false) {
    return value;
  }
  if (value === 'true' || value === 'false') {
    return value === 'true';
  }
  if (value === 1 || value === 0) {
    return value === 1;
  }
  if (value == null) {
    return defaultValue;
  }
  return Boolean(value);
};

export function resolveIncludeInScriptRequestPatientOverride({
  drugIncludeInScriptRequest,
  patientIncludeInScriptRequest,
  isNewScript = false,
  serverOverride = false,
  userChanged = false,
} = {}) {
  const drugIsExplicit = hasExplicitIncludeInScriptRequest(drugIncludeInScriptRequest);
  const patientIsExplicit = hasExplicitIncludeInScriptRequest(patientIncludeInScriptRequest);

  let globalInclude = true;
  if (drugIsExplicit) {
    globalInclude = toIncludeInScriptRequestBoolean(drugIncludeInScriptRequest, true);
  } else if (patientIsExplicit) {
    globalInclude = toIncludeInScriptRequestBoolean(patientIncludeInScriptRequest, true);
  }

  const patientInclude = patientIsExplicit
    ? toIncludeInScriptRequestBoolean(patientIncludeInScriptRequest, globalInclude)
    : globalInclude;
  const serverIsOverride = toIncludeInScriptRequestBoolean(serverOverride, false);
  const inheritGlobal = Boolean(isNewScript) && !serverIsOverride && !userChanged;
  const includeInScriptRequestValue = inheritGlobal ? globalInclude : patientInclude;

  return {
    globalInclude,
    includeInScriptRequestValue,
    showPatientOverride: includeInScriptRequestValue !== globalInclude,
  };
}

export function mapPrescriptionIncludeInScriptRequest(data = {}, { inheritGlobal = false } = {}) {
  const nestedDrugInclude =
    data.drug && typeof data.drug === 'object' ? readDrugIncludeInScriptRequestFromDrug(data.drug) : undefined;
  const rawDrug = data.drugIncludeInScriptRequest ?? data.DrugIncludeInScriptRequest ?? nestedDrugInclude;
  const rawPatient = data.includeInScriptRequest ?? data.IncludeInScriptRequest;
  const serverOverride = toIncludeInScriptRequestBoolean(
    data.isIncludeInScriptRequestPatientOverride ?? data.IsIncludeInScriptRequestPatientOverride,
    false,
  );
  const resolved = resolveIncludeInScriptRequestPatientOverride({
    drugIncludeInScriptRequest: rawDrug,
    patientIncludeInScriptRequest: rawPatient,
    isNewScript: inheritGlobal,
    serverOverride,
    userChanged: false,
  });
  const drugIsExplicit = hasExplicitIncludeInScriptRequest(rawDrug);

  return {
    includeInScriptRequest: resolved.includeInScriptRequestValue,
    drugIncludeInScriptRequest: drugIsExplicit ? resolved.globalInclude : resolved.includeInScriptRequestValue,
    isIncludeInScriptRequestPatientOverride: resolved.showPatientOverride,
  };
}

export const resolveIncludeInScriptRequest = (drugIncludeInScriptRequest, patientIncludeInScriptRequest) =>
  patientIncludeInScriptRequest ?? drugIncludeInScriptRequest ?? true;

export const isPatientOverrideIncludeInScriptRequest = (drugIncludeInScriptRequest, patientIncludeInScriptRequest) => {
  const drugInclude = toIncludeInScriptRequestBoolean(drugIncludeInScriptRequest, true);
  const patientInclude = toIncludeInScriptRequestBoolean(patientIncludeInScriptRequest, drugInclude);
  return patientInclude !== drugInclude;
};

export const shouldShowIncludeInScriptRequestPatientOverride = ({
  isCreate,
  serverOverride,
  drugIncludeInScriptRequest,
  patientIncludeInScriptRequest,
  includeChangedFromServer,
}) =>
  resolveIncludeInScriptRequestPatientOverride({
    drugIncludeInScriptRequest,
    patientIncludeInScriptRequest,
    isNewScript: isCreate,
    serverOverride,
    userChanged: includeChangedFromServer,
  }).showPatientOverride;
